<template>
    <div class="patient-alerts-container">
        <!-- Header con búsqueda y botón crear (oculto si showHeader es false) -->
        <div v-if="showHeader" class="alerts-header">
            <div class="header-title">
                <div class="icon-wrapper">
                    <i class="fas fa-bell"></i>
                </div>
                <div class="title-content">
                    <h3>Alertas del Paciente</h3>
                    <span v-if="alertsCount > 0" class="subtitle">
                        {{ alertsCount }} {{ alertsCount === 1 ? 'alerta activa' : 'alertas activas' }}
                    </span>
                </div>
            </div>

            <div class="header-actions">
                <SearchFilterClose
v-model="searchQuery" placeholder="Buscar en alertas..."
                    @update:model-value="handleSearch" />

                <button class="btn-create-alert" @click="openCreateModal">
                    <i class="fas fa-plus"></i>
                    <span>Nueva Alerta</span>
                </button>
            </div>
        </div>

        <!-- Header simplificado para panel -->
        <div v-else class="alerts-header-simple">
            <div class="header-actions-simple">
                <SearchFilterClose
v-model="searchQuery" placeholder="Buscar alertas..."
                    @update:model-value="handleSearch" />

                <button class="btn-create-alert" @click="openCreateModal">
                    <i class="fas fa-plus"></i>
                    <span>Nueva</span>
                </button>
            </div>
        </div>

        <!-- Lista de alertas -->
        <div v-if="loading && alerts.length === 0" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Cargando alertas...</p>
        </div>

        <div v-else-if="error" class="error-state">
            <i class="fas fa-exclamation-circle"></i>
            <p>{{ error }}</p>
            <button class="btn-retry" @click="loadAlerts">Reintentar</button>
        </div>

        <div v-else-if="!hasAlerts" class="empty-state">
            <div class="empty-icon-container">
                <div class="empty-icon-bg"></div>
                <i class="fas fa-bell-slash"></i>
            </div>
            <h4>No hay alertas registradas</h4>
            <p>Aún no se han creado alertas para este paciente.<br>Comienza agregando la primera alerta médica.</p>
            <button class="btn-create-first" @click="openCreateModal">
                <i class="fas fa-plus"></i>
                <span>Crear Primera Alerta</span>
            </button>
        </div>

        <div v-else class="alerts-list">
            <div v-for="alert in alerts" :key="alert.id" class="alert-card" @click="viewAlert(alert)">
                <div class="alert-icon">
                    <i class="fas fa-exclamation-triangle"></i>
                </div>

                <div class="alert-content">
                    <h4 class="alert-title">{{ alert.title }}</h4>
                    <p class="alert-description">{{ truncateText(alert.description, 100) }}</p>
                    <div class="alert-meta">
                        <span class="alert-date">
                            <i class="far fa-clock"></i>
                            {{ formatDate(alert.created_at) }}
                        </span>
                    </div>
                </div>

                <div class="alert-actions" @click.stop>
                    <button class="btn-icon" title="Editar" @click="openEditModal(alert)">
                        <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn-icon btn-danger" title="Eliminar" @click="confirmDelete(alert)">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </div>
        </div>

        <!-- Paginación -->
        <div v-if="pagination.last_page > 1" class="pagination">
            <button
class="btn-page" :disabled="pagination.current_page === 1"
                @click="changePage(pagination.current_page - 1)">
                <i class="fas fa-chevron-left"></i>
            </button>

            <span class="page-info">
                Página {{ pagination.current_page }} de {{ pagination.last_page }}
            </span>

            <button
class="btn-page" :disabled="pagination.current_page === pagination.last_page"
                @click="changePage(pagination.current_page + 1)">
                <i class="fas fa-chevron-right"></i>
            </button>
        </div>

        <!-- Modal Crear/Editar -->
        <BaseModal
v-model="showModal" :title="isEditing ? 'Editar Alerta' : 'Nueva Alerta'" icon="fas fa-bell"
            :confirm-text="isEditing ? 'Actualizar' : 'Crear'" confirm-icon="fas fa-save" :loading="loading"
            @confirm="saveAlert" @close="closeModal">
            <div class="form-group">
                <label for="alert-title">
                    Título <span class="required">*</span>
                </label>
                <input
id="alert-title" v-model="formData.title" type="text" class="form-control"
                    placeholder="Ej: Alergia a Penicilina" maxlength="255"
                    :class="{ 'is-invalid': formErrors.title }" />
                <span v-if="formErrors.title" class="error-message">
                    {{ formErrors.title }}
                </span>
            </div>

            <div class="form-group">
                <label for="alert-description">
                    Descripción <span class="required">*</span>
                </label>
                <textarea
id="alert-description" v-model="formData.description" class="form-control" rows="4"
                    placeholder="Describe la alerta con detalle..."
                    :class="{ 'is-invalid': formErrors.description }"></textarea>
                <span v-if="formErrors.description" class="error-message">
                    {{ formErrors.description }}
                </span>
            </div>
        </BaseModal>

        <!-- Modal Confirmar Eliminación -->
        <BaseModal
v-model="showDeleteModal" title="Confirmar Eliminación"
            icon="fas fa-exclamation-triangle text-danger" :loading="loading" @close="closeDeleteModal">
            <p class="warning-text">¿Estás seguro de eliminar esta alerta?</p>
            <div class="alert-preview">
                <strong>{{ alertToDelete?.title }}</strong>
                <p>{{ alertToDelete?.description }}</p>
            </div>
            <p class="text-muted">Esta acción se puede revertir posteriormente.</p>

            <template #footer>
                <button class="btn-secondary" @click="closeDeleteModal">
                    Cancelar
                </button>
                <button class="btn-danger" :disabled="loading" @click="handleDelete">
                    <i v-if="loading" class="fas fa-spinner fa-spin"></i>
                    <i v-else class="fas fa-trash"></i>
                    Eliminar
                </button>
            </template>
        </BaseModal>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { usePatientAlerts } from '@/composables/usePatientAlerts';
import SearchFilterClose from '@/components/SearchFilterClose.vue';
import BaseModal from '@/components/BaseModal.vue';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const props = defineProps({
    patientId: {
        type: Number,
        required: true
    },
    showHeader: {
        type: Boolean,
        default: false
    }
});

const {
    alerts,
    loading,
    error,
    pagination,
    hasAlerts,
    alertsCount,
    fetchAlertsByPatient,
    createAlert,
    updateAlert,
    deleteAlert
} = usePatientAlerts();

// State
const searchQuery = ref('');
const showModal = ref(false);
const showDeleteModal = ref(false);
const isEditing = ref(false);
const alertToDelete = ref(null);
const formData = ref({
    title: '',
    description: ''
});
const formErrors = ref({});

// Methods
const loadAlerts = async (page = 1) => {
    try {
        await fetchAlertsByPatient(props.patientId, {
            page,
            search: searchQuery.value,
            per_page: 10,
            sort_column: 'created_at',
            sort_order: 'desc'
        });
    } catch (err) {
        console.error('❌ Error loading alerts:', err);
    }
};

const handleSearch = () => {
    loadAlerts(1);
};

const changePage = (page) => {
    loadAlerts(page);
};

const openCreateModal = () => {
    isEditing.value = false;
    formData.value = {
        title: '',
        description: ''
    };
    formErrors.value = {};
    showModal.value = true;
};

const openEditModal = (alert) => {
    isEditing.value = true;
    formData.value = {
        id: alert.id,
        title: alert.title,
        description: alert.description
    };
    formErrors.value = {};
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    formData.value = {
        title: '',
        description: ''
    };
    formErrors.value = {};
};

const validateForm = () => {
    formErrors.value = {};
    let isValid = true;

    if (!formData.value.title || formData.value.title.trim() === '') {
        formErrors.value.title = 'El título es requerido';
        isValid = false;
    }

    if (!formData.value.description || formData.value.description.trim() === '') {
        formErrors.value.description = 'La descripción es requerida';
        isValid = false;
    }

    return isValid;
};

const saveAlert = async () => {
    if (!validateForm()) {
        return;
    }

    try {
        if (isEditing.value) {
            await updateAlert(formData.value.id, {
                title: formData.value.title,
                description: formData.value.description
            });
        } else {
            await createAlert({
                patient_id: props.patientId,
                title: formData.value.title,
                description: formData.value.description
            });
        }

        closeModal();
        loadAlerts(pagination.value.current_page);
    } catch (err) {
        console.error('Error saving alert:', err);
    }
};

const viewAlert = (alert) => {
    openEditModal(alert);
};

const confirmDelete = (alert) => {
    alertToDelete.value = alert;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    alertToDelete.value = null;
};

const handleDelete = async () => {
    try {
        await deleteAlert(alertToDelete.value.id);
        closeDeleteModal();
        loadAlerts(pagination.value.current_page);
    } catch (err) {
        console.error('Error deleting alert:', err);
    }
};

const formatDate = (date) => {
    if (!date) return '';
    return format(new Date(date), "d 'de' MMMM 'de' yyyy, h:mm a", { locale: es });
};

const truncateText = (text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

// Lifecycle
onMounted(() => {
    loadAlerts();
});

// Watchers
// eslint-disable-next-line no-unused-vars
watch(() => props.patientId, (newId, oldId) => {
    loadAlerts();
});
</script>

<style scoped>
.patient-alerts-container {
    padding: 0;
    background: transparent;
    min-height: 500px;
}

/* ========================================
   HEADER MODERNO
======================================== */
.alerts-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid #e2e8f0;
    flex-wrap: wrap;
    gap: 1.5rem;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.icon-wrapper {
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.icon-wrapper i {
    color: white;
    font-size: 1.5rem;
}

.title-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.title-content h3 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.02em;
}

.subtitle {
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 500;
}

.alerts-header-simple {
    margin-bottom: 1.5rem;
}

.header-actions,
.header-actions-simple {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 1rem;
    align-items: center;
}

.btn-create-alert {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
    white-space: nowrap;
}

.btn-create-alert:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.btn-create-alert:active {
    transform: translateY(0);
}

.btn-create-alert i {
    font-size: 1rem;
}

/* ========================================
   ESTADOS (Loading, Error, Empty)
======================================== */
.loading-state,
.error-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
}

.loading-state i {
    font-size: 3.5rem;
    color: #667eea;
    margin-bottom: 1.5rem;
}

.error-state i {
    font-size: 3.5rem;
    color: #ef4444;
    margin-bottom: 1.5rem;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 6rem 2rem;
    text-align: center;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 20px;
    border: 2px dashed #e2e8f0;
    margin: 2rem 0;
}

.empty-icon-container {
    position: relative;
    margin-bottom: 2rem;
}

.empty-icon-bg {
    position: absolute;
    width: 140px;
    height: 140px;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 0.6;
    }

    50% {
        transform: translate(-50%, -50%) scale(1.1);
        opacity: 0.8;
    }
}

.empty-state i {
    font-size: 4.5rem;
    color: #667eea;
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 4px 8px rgba(102, 126, 234, 0.2));
}

.empty-state h4 {
    margin: 0.5rem 0;
    color: #1e293b;
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
}

.empty-state p {
    color: #64748b;
    margin-bottom: 2.5rem;
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
}

.btn-retry,
.btn-create-first {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 14px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
}

.btn-retry:hover,
.btn-create-first:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(102, 126, 234, 0.45);
}

.btn-create-first i {
    font-size: 1.125rem;
    color: white;
}

/* ========================================
   LISTA DE ALERTAS MODERNA
======================================== */
.alerts-list {
    display: grid;
    gap: 1rem;
}

.alert-card {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
    background: white;
    border-radius: 16px;
    border: 2px solid #f1f5f9;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.alert-card::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.alert-card:hover {
    border-color: #667eea;
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
    transform: translateY(-2px);
}

.alert-card:hover::before {
    opacity: 1;
}

.alert-icon {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border-radius: 14px;
    position: relative;
}

.alert-icon::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.alert-card:hover .alert-icon::after {
    opacity: 0.6;
}

.alert-icon i {
    color: #f59e0b;
    font-size: 1.75rem;
    position: relative;
    z-index: 1;
}

.alert-content {
    flex: 1;
    min-width: 0;
}

.alert-title {
    margin: 0 0 0.5rem 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.01em;
    line-height: 1.4;
}

.alert-description {
    margin: 0 0 0.875rem 0;
    color: #64748b;
    line-height: 1.6;
    font-size: 0.9375rem;
}

.alert-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.alert-date {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #94a3b8;
    font-weight: 500;
}

.alert-date i {
    font-size: 0.875rem;
}

.alert-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
}

.btn-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-icon:hover {
    background: #667eea;
    border-color: #667eea;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-icon.btn-danger:hover {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border-color: #ef4444;
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* ========================================
   PAGINACIÓN MODERNA
======================================== */
.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 2px solid #f1f5f9;
}

.btn-page {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-page:hover:not(:disabled) {
    background: #667eea;
    border-color: #667eea;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-page:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background: #f8fafc;
}

.page-info {
    color: #64748b;
    font-size: 0.9375rem;
    font-weight: 600;
    padding: 0 0.5rem;
}

/* ========================================
   ESTILOS ESPECÍFICOS DE ALERTA
======================================== */
.alert-preview {
    padding: 1.5rem;
    background: linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    margin: 1.5rem 0;
    border: 2px solid #e2e8f0;
}

.alert-preview strong {
    display: block;
    margin-bottom: 0.75rem;
    color: #1e293b;
    font-size: 1.125rem;
    font-weight: 700;
}

.alert-preview p {
    margin: 0;
    color: #64748b;
    font-size: 0.9375rem;
    line-height: 1.6;
}

.warning-text {
    text-align: center;
    color: #64748b;
    margin-bottom: 1.5rem;
    font-size: 1rem;
}

.text-muted {
    text-align: center;
    color: #94a3b8;
    font-size: 0.875rem;
}

.text-danger {
    color: #ef4444;
}

/* ========================================
   ESTILOS DE FORMULARIO
======================================== */
.form-group {
    margin-bottom: 1.75rem;
}

.form-group:last-child {
    margin-bottom: 0;
}

.form-group label {
    display: block;
    margin-bottom: 0.75rem;
    font-weight: 600;
    color: #334155;
    font-size: 0.9375rem;
}

.required {
    color: #ef4444;
    margin-left: 0.25rem;
}

.form-control {
    width: 100%;
    padding: 0.875rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 0.9375rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: inherit;
}

.form-control:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
    border-color: #ef4444;
    background-color: #fef2f2;
}

.form-control.is-invalid:focus {
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
}

.error-message {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    color: #ef4444;
    font-size: 0.875rem;
    font-weight: 500;
}

.error-message::before {
    content: '⚠';
    font-size: 1rem;
}

textarea.form-control {
    resize: vertical;
    min-height: 120px;
    line-height: 1.6;
}

.btn-secondary,
.btn-primary,
.btn-danger {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.875rem 1.75rem;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    font-size: 0.9375rem;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-secondary {
    background: #f1f5f9;
    color: #64748b;
    border: 2px solid #e2e8f0;
}

.btn-secondary:hover {
    background: #e2e8f0;
    border-color: #cbd5e1;
}

.btn-danger {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.btn-danger:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
}

.btn-danger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.btn-danger i,
.btn-secondary i {
    font-size: 1rem;
}

/* ========================================
   RESPONSIVE
======================================== */
@media (max-width: 768px) {
    .alerts-header {
        flex-direction: column;
        align-items: stretch;
        gap: 1rem;
    }

    .header-title {
        justify-content: space-between;
    }

    .icon-wrapper {
        width: 48px;
        height: 48px;
    }

    .icon-wrapper i {
        font-size: 1.25rem;
    }

    .title-content h3 {
        font-size: 1.25rem;
    }

    .header-actions,
    .header-actions-simple {
        flex-direction: column;
        align-items: stretch;
    }

    .btn-create-alert {
        justify-content: center;
        width: 100%;
    }

    .modal-content {
        width: 95%;
        margin: 1rem;
        max-height: 85vh;
    }

    .modal-header {
        padding: 1.5rem;
    }

    .modal-header h3 {
        font-size: 1.25rem;
    }

    .modal-header h3 i {
        width: 40px;
        height: 40px;
        font-size: 1rem;
    }

    .modal-body {
        padding: 1.5rem;
    }

    .modal-footer {
        padding: 1rem 1.5rem;
        flex-wrap: wrap;
    }

    .btn-secondary,
    .btn-primary,
    .btn-danger {
        flex: 1;
        justify-content: center;
        min-width: 0;
    }

    .alert-card {
        flex-direction: column;
        gap: 1rem;
    }

    .alert-actions {
        align-self: flex-end;
        width: 100%;
        justify-content: flex-end;
    }

    .alert-icon {
        width: 48px;
        height: 48px;
    }

    .alert-icon i {
        font-size: 1.5rem;
    }

    .pagination {
        gap: 0.5rem;
    }

    .page-info {
        font-size: 0.875rem;
    }
}

@media (max-width: 480px) {
    .alert-title {
        font-size: 1rem;
    }

    .alert-description {
        font-size: 0.875rem;
    }

    .btn-icon {
        width: 36px;
        height: 36px;
    }
}
</style>
